import { SystemConfig, SystemConfigSavePayload } from '@ekkogmbh/apisdk';
import { Image, RestartAlt, Save, Settings } from '@mui/icons-material';
import { Box, Button, Divider, Paper, Popover, Stack, Switch, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { LoadingMask } from '../../Common/Components/LoadingMask';
import { spacing } from '../../Common/Helper/ThemeHelper';
import { ContentControl } from 'src/Common/Components/ContentControl';
import StyledDropzone from 'src/Common/Components/Forms/StyledDropzone';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';

const TitleTypography = styled(Typography)({
  fontSize: 28,
  fontWeight: 700,
});

const SectionTitle = styled(Typography)({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: 16,
});

const ConfigButton = styled(Button)(({ theme }) => ({
  flexGrow: 1,
  margin: spacing(theme),
}));

export const SystemConfigContent = (): React.JSX.Element => {
  const { api } = useEkko();

  const [isLoadingConfig, startLoadingConfig] = useRequest<SystemConfig>();
  const [isLoadingImage, startLoadingImage] = useRequest<Blob>();
  const [isSaving, startSaving] = useRequest();
  const [popoverState, setPopoverState] = useState<{
    anchor: HTMLButtonElement;
    inputValue?: string;
  }>();

  /** coordinate is not used anywhere ?? */
  const [, setCoordinate] = useState<string>('');
  const [imageFile, setImageFile] = useState<File>();
  const [imageBlob, setImageBlob] = useState<Blob>();
  const [imageType, setImageType] = useState<string>('');
  const [isEnabled, setEnabled] = useState<boolean>(false);
  const [primaryColor, setPrimaryColor] = useState<string>('');

  const reset = useCallback((config?: SystemConfig, clearImage: boolean = false) => {
    setCoordinate(config?.coordinate ?? '');
    setEnabled(config?.enabled ?? false);
    setImageType(config?.imageType ?? '');
    // Add # prefix for color picker (backend stores without #)
    const color = config?.workerUiPrimaryColor;
    setPrimaryColor(color ? `#${color}` : '');
    if (clearImage) {
      setImageFile(undefined);
      setImageBlob(undefined);
    }
  }, []);

  const fetchConfig = useCallback(() => {
    startLoadingConfig(
      () => api.getSystemConfigRoot(),
      (response) => reset(response),
      () => reset(),
    );
  }, [api, reset]);

  const fetchImageBlob = useCallback(() => {
    startLoadingImage(
      () => api.getSystemConfigRootImage(),
      (response) => setImageBlob(response),
      () => setImageBlob(undefined),
    );
  }, [api]);

  useEffect(() => {
    fetchConfig();
    fetchImageBlob();
  }, []);

  const save = useCallback(() => {
    // Strip # from color for backend (expects format like FFA626)
    const colorForBackend = primaryColor ? primaryColor.replace(/^#/, '') : null;
    const payload: SystemConfigSavePayload = {
      enabled: isEnabled,
      workerUiPrimaryColor: colorForBackend,
    };

    const savableImage =
      imageFile !== undefined
        ? imageFile
        : imageBlob !== undefined
          ? new File([imageBlob], 'image.' + (imageType === 'image/png' ? 'png' : 'svg'))
          : null;
    startSaving(() => api.saveSystemConfigRoot(payload, savableImage));
  }, [isEnabled, primaryColor, imageFile, imageBlob, imageType]);

  const isLoading = useMemo(
    () => isSaving || isLoadingConfig || isLoadingImage,
    [isSaving, isLoadingConfig, isLoadingImage],
  );

  const popover = useMemo(() => {
    if (popoverState === undefined) {
      return <></>;
    }

    return (
      <Popover
        open
        anchorEl={popoverState.anchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'center' }}
        transformOrigin={{ horizontal: 'center', vertical: 'center' }}
        onClose={() => setPopoverState(undefined)}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <HexColorPicker
            color={popoverState.inputValue}
            onChange={(color) => {
              setPopoverState({ ...popoverState, inputValue: color });
            }}
          />
          <StyledTextField
            label="Hex Color"
            value={popoverState.inputValue ?? ''}
            onChange={(e) => {
              setPopoverState({ ...popoverState, inputValue: e.target.value });
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (popoverState?.inputValue === undefined || popoverState.inputValue === '') {
                return;
              }

              setPrimaryColor(popoverState.inputValue);
              setPopoverState(undefined);
            }}
          >
            Confirm
          </Button>
        </Stack>
      </Popover>
    );
  }, [popoverState]);

  const imageUrl = useMemo(() => {
    const urlInput = imageFile ?? imageBlob;
    if (urlInput === undefined) {
      return;
    }

    return URL.createObjectURL(urlInput);
  }, [imageFile, imageBlob]);

  return (
    <ContentControl>
      <Box component={'div'} sx={{ p: 3 }}>
        {isLoading && <LoadingMask width={75} height={75} />}

        <TitleTypography color="textPrimary" sx={{ mb: 3 }}>
          Root System Configuration
        </TitleTypography>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <SectionTitle color="textPrimary" sx={{ mb: 0 }}>
              Worker-UI White-Labeling
            </SectionTitle>
            <Switch checked={isEnabled} onChange={() => setEnabled(!isEnabled)} color="primary" />
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            alignItems="stretch"
            sx={{
              opacity: isEnabled ? 1 : 0.5,
              pointerEvents: isEnabled ? 'auto' : 'none',
              transition: 'opacity 0.2s',
            }}
          >
            {/* Logo Dropzone */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Image fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Logo
                </Typography>
              </Stack>
              <StyledDropzone
                imageUrl={imageUrl ?? undefined}
                instructions={imageUrl ? undefined : 'Drop a logo file here, or click to select'}
                accept="image/svg+xml, .svg, image/png, .png"
                onDrop={(f) => setImageFile(f[0])}
                onFileDialogCancel={() => setImageFile(undefined)}
                multiple={false}
              />
            </Box>

            {/* Settings */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Settings fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Settings
                </Typography>
              </Stack>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {/* Primary Color */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>Primary Color</Typography>
                    <Box
                      onClick={(event) => {
                        setPopoverState({
                          anchor: event.currentTarget as unknown as HTMLButtonElement,
                          inputValue: primaryColor,
                        });
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: primaryColor || 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    />
                    {popover}
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </Paper>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <ConfigButton
            variant="outlined"
            color="secondary"
            onClick={() => reset(undefined, true)}
            startIcon={<RestartAlt />}
            sx={{ flexGrow: 0 }}
          >
            Reset
          </ConfigButton>
          <ConfigButton
            variant="contained"
            color="primary"
            onClick={() => save()}
            endIcon={<Save />}
            sx={{ flexGrow: 0 }}
          >
            Save
          </ConfigButton>
        </Stack>
      </Box>
    </ContentControl>
  );
};
